import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button, StatusView } from '@/components/ui';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches a render-time crash anywhere in the tree so a single broken record
 * cannot take the whole application down to a blank page.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled error in ELA Lab:', error, info.componentStack);
  }

  override render() {
    const { error } = this.state;
    if (error === null) return this.props.children;

    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100dvh' }}>
        <StatusView
          icon="alert"
          tone="danger"
          title="Something went wrong"
          message={error.message || 'An unexpected error stopped the page from rendering.'}
          action={
            <Button variant="primary" icon="refresh" onClick={() => window.location.reload()}>
              Reload ELA Lab
            </Button>
          }
        />
      </div>
    );
  }
}
